import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import type { AxiosError } from 'axios'
import { filesApi } from '../../services/api'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { useAuthStore } from '../../store/authStore'

export type FileApiError = AxiosError<Record<string, unknown> | string>

/**
 * Step 1 of the presigned upload flow: ask for an upload URL, sending the
 * file's SHA-256. Rejects bad extension / oversize here, before a byte is
 * uploaded, and may answer with an already-uploaded file. Goes through
 * apiClient (auth + org header) — unlike the PUT hop that follows it.
 */
export function usePresignFile() {
  return useMutation({
    mutationFn: (file: { filename: string; size: number; sha256: string }) =>
      filesApi.presign(file.filename, file.size, file.sha256),
  })
}

/**
 * Step 3 of the presigned upload flow: confirm a completed upload. The
 * checksum was signed at presign, so the backend only HEADs the object — fast
 * for any size. Callers still show a non-cancellable "verifying" state while
 * this is in flight.
 */
export function useConfirmFile() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (uploadId: string) => filesApi.confirm(uploadId),
    onSuccess: () => {
      // A confirmed upload is a scannable asset like any other — refresh the
      // list Create Scan step 1 picks from.
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allScannableAssets(orgId) })
    },
  })
}

/** The server decides what may be uploaded; the UI has no file-type literals. */
export function useFileTypes() {
  return useQuery({
    queryKey: ['file-types'],
    queryFn: filesApi.types,
    staleTime: Infinity,
  })
}

/**
 * The org's uploaded files — grade and severity counts ride on each row.
 * Driven by the URL (`search`, `kind`, `ordering`, `page`), like the other lists.
 */
export function useFiles() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()
  return useQuery({
    queryKey: queryKeys.files.list(orgId, params),
    queryFn: () => filesApi.getList(params),
    enabled: !!orgId,
  })
}

/** One file, for the detail page's identity. */
export function useFileDetail(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.files.detail(orgId, id ?? ''),
    queryFn: () => filesApi.getById(id as string),
    enabled: !!id && !!orgId,
    retry: false,
  })
}
